// SessionForm.js - VERSION CORRIGÉE avec migration participants

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { ProtectedModal, useCollaborationContext } from '../CollaborationProvider';
import { useNotifications } from '../../contexts/NotificationContext';

// Hooks personnalisés
import useSessionForm from '../../hooks/useSessionForm';
import useSessionSaving from '../../hooks/useSessionSaving';
import useMultiDayData from '../../hooks/useMultiDayData';
import useConflictHandling from '../../hooks/useConflictHandling';

// Composants UI
import SessionHeader from './SessionHeader';
import TabNavigation from './TabNavigation';
import BasicInfoPanel from './BasicInfoPanel';
import ParticipantsPanel from './ParticipantsPanel';
import OptionsPanel from './OptionsPanel';
import SessionFooter from './SessionFooter';
import NotificationModal from './NotificationModal';
import { WorksheetSelector } from '../DailyWorksheet';
import { EntityHistory } from '../Analytics';

// Utilitaires
import { validateBeforeSave } from '../../utils/validationUtils';
import { PRESTATION_TYPES, SESSION_TYPES } from '../../utils/constants';

// Styles CSS
import '../../styles/sessionForm/index.css';

/**
 * Composant SessionForm principal refactorisé
 * Version corrigée avec migration participants classique → multi-jours
 */
const SessionForm = ({ token, editingSession, onClose, onSave }) => {
  // ✅ PROTECTION : Ref pour éviter les cycles infinis
  const componentId = useRef(Math.random().toString(36).substr(2, 9));
  const initializationRef = useRef(false);
  const timeoutRef = useRef(null);
  const multiDayLoadedRef = useRef(false);
  const syncInProgressRef = useRef(false); // ✅ PROTECTION anti-boucle partagée
  const userHasModifiedRef = useRef(false); // ✅ PROTECTION : Ne pas vérifier conflits au chargement initial
  
/*  console.log('🔄 SessionForm render - ID:', componentId.current);*/

  // Hook de collaboration
  const sendCollaborationMessage = (() => {
    try {
      const context = useCollaborationContext();
      return context.sendCollaborationMessage || context.sendMessage || (() => {});
    } catch (error) {
      console.warn('Erreur contexte collaboration:', error);
      return () => {};
    }
  })();

  // Hook de notifications
  const { showSuccess, showError, showWarning, showInfo, showConfirmation } = useNotifications();

  // ✅ STABILISER les fonctions de notification avec useCallback
  const stableShowInfo = useCallback((message, options) => {
    if (showInfo) showInfo(message, options);
  }, [showInfo]);

  const stableShowWarning = useCallback((message, options) => {
    if (showWarning) showWarning(message, options);
  }, [showWarning]);

  const stableShowError = useCallback((message, options) => {
    if (showError) showError(message, options);
  }, [showError]);

  // États locaux pour la gestion des onglets
  const [activeTab, setActiveTab] = useState('basic');
  const [isReady, setIsReady] = useState(false);
  const [showWorksheets, setShowWorksheets] = useState(false);
  const [showNotificationModal, setShowNotificationModal] = useState(false);

  // Hook principal du formulaire
  const {
    formData,
    setFormData,
    handleChange,
    // Nouveau nommage
    groups,
    clients,
    locations,
    teamLeaders,
    // Alias pour compatibilite
    rooms,
    trainers,
    users,
    teams,
    statuses,
    loading: formLoading,
    error: formError,
    setError: setFormError,
    resetForm
  } = useSessionForm(token, editingSession, stableShowError, stableShowInfo);

  // Hook de gestion multi-jours
  const {
    sessionDays,
    setSessionDays,
    dayParticipants,
    setDayParticipants,
    participantOnSite,
    setParticipantOnSite,
    getParticipantOnSite,
    setParticipantOnSiteFlag,
    dayTeamLeaders,
    setDayTeamLeaders,
    activeDayTab,
    setActiveDayTab,
    multiDayInfo,
    loadMultiDayData,
    resetMultiDayData
  } = useMultiDayData(formData, editingSession, token, stableShowInfo, stableShowError,setFormData);

  // Hook de gestion des conflits
  const {
    conflicts,
    setConflicts,
    checkConflictsDebounced,
    checkParticipantConflicts,
    resolveConflicts,
    cleanup: cleanupConflicts
  } = useConflictHandling(token, editingSession);

  // Hook de sauvegarde
  const {
    handleSubmit: submitSession,
    loading: savingLoading
  } = useSessionSaving({
    token,
    editingSession,
    formData,
    multiDayInfo,
    conflicts,
    showSuccess,
    showError,
    showConfirmation,
    onSave,
    onClose
  });

  // État global de chargement
  const loading = formLoading || savingLoading;

  // ✅ WRAPPER : Marquer les modifications utilisateur pour déclencher les vérifications de conflits
  const handleChangeWithTracking = useCallback((e) => {
    userHasModifiedRef.current = true;
    handleChange(e);
  }, [handleChange]);

  // ✅ NOUVEAU : Ajouter automatiquement le chef d'équipe aux participants de chaque jour
  // SAUF si un override de chef d'équipe est défini pour ce jour
  useEffect(() => {
    const defaultTeamLeaderId = formData.team_leader_id || formData.trainer_id;

    if (!defaultTeamLeaderId || !sessionDays || sessionDays.length === 0) {
      return;
    }

    // Vérifier si le chef d'équipe est déjà dans les participants de chaque jour
    let needsUpdate = false;
    const updatedDayParticipants = { ...dayParticipants };

    sessionDays.forEach(day => {
      // Si un override existe pour ce jour, utiliser l'override, pas le chef par défaut
      const dayOverride = dayTeamLeaders[day.date];
      const effectiveTeamLeaderId = dayOverride || defaultTeamLeaderId;

      const dayParts = updatedDayParticipants[day.date] || [];

      // N'ajouter que le chef EFFECTIF de ce jour (pas le défaut si override)
      if (!dayParts.includes(effectiveTeamLeaderId)) {
        updatedDayParticipants[day.date] = [...dayParts, effectiveTeamLeaderId];
        needsUpdate = true;
        console.log(`[SessionForm] Chef d'equipe effectif ${effectiveTeamLeaderId} ajoute au jour ${day.date} (override: ${!!dayOverride})`);
      }
    });

    if (needsUpdate) {
      setDayParticipants(updatedDayParticipants);
    }
  }, [formData.team_leader_id, formData.trainer_id, sessionDays, dayParticipants, setDayParticipants, dayTeamLeaders]);

  // ✅ WRAPPER : setDayParticipants avec warning si chef d'équipe retiré
  const setDayParticipantsWithWarning = useCallback((updater) => {
    const teamLeaderId = formData.team_leader_id || formData.trainer_id;

    // Appliquer la mise à jour
    setDayParticipants(prev => {
      const newValue = typeof updater === 'function' ? updater(prev) : updater;

      // Vérifier si le chef d'équipe a été retiré d'un jour
      if (teamLeaderId) {
        Object.keys(newValue).forEach(date => {
          const oldParticipants = prev[date] || [];
          const newParticipants = newValue[date] || [];

          // Si le chef d'équipe était présent et ne l'est plus
          if (oldParticipants.includes(teamLeaderId) && !newParticipants.includes(teamLeaderId)) {
            // Afficher le warning (setTimeout pour éviter setState pendant render)
            setTimeout(() => {
              stableShowWarning(
                'Attention : vous retirez le chef d\'equipe des participants de ce jour',
                { title: 'Chef d\'equipe retire', duration: 4000 }
              );
            }, 0);
          }
        });
      }

      return newValue;
    });
  }, [formData.team_leader_id, formData.trainer_id, setDayParticipants, stableShowWarning]);

  // ✅ WRAPPER : setFormData avec tracking
  const setFormDataWithTracking = useCallback((updater) => {
    userHasModifiedRef.current = true;
    setFormData(updater);
  }, [setFormData]);

  /*
  // ✅ SURVEILLANCE des re-renders
  useEffect(() => {
    console.log('🔄 SessionForm useEffect déclenché - isReady:', isReady, 'formLoading:', formLoading);
  }, [isReady, formLoading]);
*/

  /*
  // ✅ NOUVEAU : Détecter passage de classique à multi-jours et migrer participants
  useEffect(() => {
    console.log('🔍 === DÉTECTION MIGRATION PARTICIPANTS ===');
    console.log('📅 multiDayInfo.isMultiDay:', multiDayInfo.isMultiDay);
    console.log('👥 formData.participant_ids:', formData.participant_ids);
    console.log('📋 sessionDays.length:', sessionDays.length);
    console.log('🗂️ dayParticipants:', dayParticipants);
    
    // ✅ CONDITION : Formation devient multi-jours ET il y a des participants classiques
    if (multiDayInfo.isMultiDay && 
        formData.participant_ids && 
        formData.participant_ids.length > 0 && 
        sessionDays.length > 0) {
      
      // Vérifier si les participants sont déjà migrés
      const hasParticipantsInDays = Object.values(dayParticipants).some(dayParts => dayParts && dayParts.length > 0);
      
      console.log('🔍 hasParticipantsInDays:', hasParticipantsInDays);
      
      if (!hasParticipantsInDays) {
        console.log('🔄 === MIGRATION PARTICIPANTS CLASSIQUE → MULTI-JOURS ===');
        console.log('👥 Participants à migrer:', formData.participant_ids);
        
        // Migrer les participants vers tous les jours
        const newDayParticipants = {};
        const newParticipantHours = {};
        
        sessionDays.forEach(day => {
          console.log(`📅 Migration vers jour ${day.date}`);
          
          // Copier tous les participants vers ce jour
          newDayParticipants[day.date] = [...formData.participant_ids];
          
          // Initialiser les heures par défaut pour tous les participants
          newParticipantHours[day.date] = {};
          formData.participant_ids.forEach(participantId => {
            newParticipantHours[day.date][participantId] = {
              training: parseFloat(formData.training_hours) || 7,
              travel: parseFloat(formData.travel_hours) || 1,
              isManual: false
            };
          });
        });
        
        console.log('✅ Migration terminée:');
        console.log('📋 newDayParticipants:', newDayParticipants);
        console.log('⏰ newParticipantHours:', newParticipantHours);
        
        // Appliquer la migration
        setDayParticipants(newDayParticipants);
        setParticipantHours(newParticipantHours);
        
        // Notification de migration
        stableShowInfo('Participants migrés vers formation multi-jours', {
          title: '🔄 Migration automatique',
          details: `${formData.participant_ids.length} participant(s) ajouté(s) à tous les jours`,
          duration: 3000
        });
      }
    }
  }, [
    multiDayInfo.isMultiDay,
    formData.participant_ids,
    sessionDays,
    dayParticipants,
    formData.training_hours,
    formData.travel_hours,
    setDayParticipants,
    setParticipantHours,
    stableShowInfo
  ]);
  */

  // ✅ INITIALISATION CORRIGÉE
  useEffect(() => {
    const initialize = async () => {
      try {

        /*
        // ✅ PROTECTION : Éviter les initialisations multiples
        if (initializationRef.current) {
          console.log('⏭️ Initialisation déjà en cours, skip');
          return;
        }
*/

        initializationRef.current = true;
        
        console.log('🚀 === INITIALISATION SESSIONFORM (STABLE) ===');
        console.log('🔍 ID composant:', componentId.current);
        
        // 🔍 DIAGNOSTIC : Vérifier l'état des données
        console.log('📊 État des données au démarrage:', {
          roomsCount: rooms.length,
          statusesCount: statuses.length,
          trainersCount: trainers.length,
          usersCount: users.length,
          teamsCount: teams.length,
          formLoading,
          formError,
          editingSessionId: editingSession?.id,
          isReady
        });

        // ✅ ATTENTE : Attendre que les données de base soient chargées
        const hasBasicData = rooms.length > 0 && statuses.length > 0;
        
        if (!hasBasicData) {
          console.log('⏳ Attente données de base...');
          
          // ✅ TIMEOUT plus court et avec nettoyage
          if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
          }
          
          timeoutRef.current = setTimeout(() => {
            if (!isReady) {
              console.warn('⚠️ Timeout - ouverture forcée');
              setIsReady(true);
              stableShowWarning('Chargement partiel', {
                title: '⚠️ Données incomplètes',
                details: 'Certaines données de référence sont manquantes'
              });
            }
            initializationRef.current = false;
          }, 6000); // 6 secondes
          
          initializationRef.current = false;
          return;
        }

        console.log('✅ Données présentes, finalisation...');
        
        // ✅ CHARGEMENT multi-jours si nécessaire
        if (editingSession?.id && !multiDayLoadedRef.current) {
          try {
            console.log('📅 Chargement multi-jours session:', editingSession.id);
            const loaded = await loadMultiDayData(editingSession.id);
            multiDayLoadedRef.current = true;
            if (loaded) {
              console.log('✅ Multi-jours chargé avec succès');
              stableShowInfo('Formation multi-jours chargée', {
                title: '📅 Mode multi-jours',
                duration: 2000
              });
            } else {
              console.log('📋 Formation classique chargée');
            }
          } catch (error) {
            console.warn('⚠️ Erreur multi-jours:', error);
            stableShowWarning('Erreur chargement multi-jours', {
              title: '⚠️ Chargement partiel',
              details: 'Les données multi-jours n\'ont pas pu être chargées'
            });
          }
        }
        
        // ✅ NETTOYAGE
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
        }
        
        setIsReady(true);
        console.log('✅ SessionForm stable et prêt');
        
      } catch (error) {
        console.error('❌ Erreur initialisation:', error);
        setFormError('Erreur d\'initialisation: ' + error.message);
        
        // ✅ RECOVERY : Forcer l'ouverture même en cas d'erreur
        setIsReady(true);
        stableShowError('Erreur d\'initialisation', {
          title: '❌ Problème de chargement',
          details: error.message
        });
      } finally {
        setTimeout(() => {
          initializationRef.current = false;
        }, 2000); // Reset après 2 secondes
      }
    };

    if (!isReady && !initializationRef.current) {
      initialize();
    }
  }, [
    rooms.length, 
    statuses.length, 
    trainers.length,
    users.length,
    teams.length,
    editingSession?.id,
    formLoading,
    formError,
    loadMultiDayData,
    stableShowInfo,
    stableShowWarning,
    stableShowError,
    setFormError
  ]);

  // ✅ NETTOYAGE : Nettoyer les timeouts au démontage
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, []);

  // ✅ STABILISER la vérification des conflits
  useEffect(() => {
    // ✅ PROTECTION : Ne pas vérifier les conflits au chargement initial
    if (!userHasModifiedRef.current) {
      console.log('⏳ Skip vérification conflits - aucune modification utilisateur');
      return;
    }

    if (!isReady || formLoading) {
      console.log('⏳ Skip vérification conflits - pas prêt ou en chargement');
      return;
    }

    // ✅ PROTECTION : Éviter les vérifications multiples
    const timeoutId = setTimeout(() => {
      if (formData.room_id && formData.start_date) {
        const shouldCheck = formData.session_type !== 'training' || formData.trainer_id;
        if (shouldCheck) {
          console.log('🔍 Vérification conflits (après modification utilisateur)');
          checkConflictsDebounced(formData);
        }
      }
    }, 500); // Délai pour éviter les appels multiples

    return () => clearTimeout(timeoutId);
  }, [
    isReady,
    formLoading,
    formData.room_id,
    formData.trainer_id,
    formData.start_date,
    formData.end_date,
    formData.session_type,
    checkConflictsDebounced
  ]);

  // MEMORISATION : Configuration des onglets
  // Onglets dynamiques selon le type de prestation :
  // - Prestation : Informations, Participants (obligatoires), Options
  // - Blocage : Informations, Participants (optionnels)
  // - Note : Informations uniquement
  // + Historique : uniquement en mode edition
  const tabs = useMemo(() => {
    const prestationType = formData.prestation_type || formData.session_type;
    const tabsList = [
      { id: 'basic', label: 'Informations', icon: 'i' }
    ];

    // Participants pour tous les types (obligatoire pour prestation, optionnel pour blocked/note)
    tabsList.push({ id: 'participants', label: 'Participants', icon: 'p' });

    // Options uniquement pour "prestation" (services, hébergement, statut)
    if (prestationType === 'prestation' || prestationType === 'training') {
      tabsList.push({ id: 'options', label: 'Options', icon: 'o' });
    }

    // Historique uniquement en mode edition + permission analytics.view ou admin
    if (editingSession?.id) {
      try {
        const base64 = token?.split('.')[1];
        if (base64) {
          const payload = JSON.parse(atob(base64));
          const perms = payload.permissions || [];
          if (perms.includes('all') || perms.includes('analytics.view')) {
            tabsList.push({ id: 'history', label: 'Historique', icon: 'h' });
          }
        }
      } catch (_) { /* token invalide — pas d'onglet historique */ }
    }

    return tabsList;
  }, [formData.prestation_type, formData.session_type, editingSession?.id, token]);

  // Reset activeTab si l'onglet actif n'est plus disponible pour ce type
  useEffect(() => {
    const prestationType = formData.prestation_type || formData.session_type;
    // Tous les types ont un onglet Participants (optionnel pour blocages/notes)
    const hasParticipantsTab = true;
    const hasOptionsTab = prestationType === 'prestation' || prestationType === 'training';

    // Si on est sur participants mais qu'il n'y a plus d'onglet participants
    if (activeTab === 'participants' && !hasParticipantsTab) {
      setActiveTab('basic');
    }
    // Si on est sur options mais qu'il n'y a plus d'onglet options
    if (activeTab === 'options' && !hasOptionsTab) {
      setActiveTab('basic');
    }
  }, [formData.prestation_type, formData.session_type, activeTab]);

  // ✅ GESTION SOUMISSION : Avec protection
  const handleSubmit = useCallback(async () => {
    try {
      console.log('📤 Soumission SessionForm');
      
      // Validation avant sauvegarde
      const validation = validateBeforeSave(formData, {
        multiDayData: { sessionDays, dayParticipants },
        conflicts
      });

      if (!validation.isValid) {
        setFormError(validation.errors[0]);
        stableShowError('Données invalides', {
          title: '❌ Validation échouée',
          details: validation.errors.join('\n')
        });
        return;
      }

      // Confirmer s'il y a des conflits
      if (validation.canSaveWithWarnings) {
        const proceed = await showConfirmation(
          'Des avertissements ont été détectés. Continuer ?',
          {
            title: '⚠️ Avertissements',
            details: validation.warnings.join('\n')
          }
        );
        if (!proceed) return;
      }

      // Sauvegarde
      await submitSession(sessionDays, dayParticipants, participantOnSite);

    } catch (error) {
      console.error('❌ Erreur soumission:', error);
      setFormError(error.message);
      stableShowError('Erreur de sauvegarde', {
        title: '❌ Échec',
        details: error.message
      });
    }
  }, [
    formData,
    sessionDays,
    dayParticipants,
    participantOnSite,
    conflicts,
    submitSession,
    showConfirmation,
    setFormError,
    stableShowError
  ]);

  // ✅ GESTION FERMETURE : Avec protection
  const handleClose = useCallback(async () => {
    try {
      console.log('🚪 Fermeture SessionForm');
      
      // Vérifier les modifications non sauvegardées
      const hasChanges = formData.room_id || formData.trainer_id || 
                        formData.participant_ids.length > 0 ||
                        formData.block_reason || formData.maintenance_description;

      if (hasChanges && !editingSession?.id) {
        const proceed = await showConfirmation(
          'Vous avez des modifications non sauvegardées. Voulez-vous vraiment fermer ?',
          {
            title: '⚠️ Modifications non sauvegardées',
            details: 'Toutes les données saisies seront perdues'
          }
        );
        
        if (!proceed) return;
      }

      // Nettoyage
      cleanupConflicts();
      resetForm();
      resetMultiDayData();
      userHasModifiedRef.current = false; // Reset du flag de modification

      // Nettoyage des timeouts
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }

      onClose();
    } catch (error) {
      console.error('❌ Erreur fermeture:', error);
      onClose(); // Forcer la fermeture même en cas d'erreur
    }
  }, [
    formData,
    editingSession?.id,
    showConfirmation,
    cleanupConflicts,
    resetForm,
    resetMultiDayData,
    onClose
  ]);

  // ✅ ÉCRAN DE CHARGEMENT amélioré
  if (!isReady) {
    return (
      <div className="sf-overlay">
        <div style={{
          background: 'white',
          borderRadius: '20px',
          padding: '60px',
          textAlign: 'center',
          maxWidth: '500px',
          boxShadow: '0 20px 60px rgba(0,0,0,0.15)'
        }}>
          <div style={{ fontSize: '64px', marginBottom: '20px' }}>
            {formLoading ? '⏳' : '🔄'}
          </div>
          <div style={{ fontSize: '18px', fontWeight: '600', marginBottom: '10px' }}>
            {formLoading ? 'Chargement en cours...' : 'Initialisation...'}
          </div>
          <div style={{ fontSize: '14px', color: '#6c757d', marginBottom: '20px' }}>
            {formLoading 
              ? 'Récupération des données de référence'
              : 'Préparation du formulaire'
            }
          </div>
          
          {/* Indicateur de progression detaille */}
          <div style={{
            fontSize: '12px',
            color: '#6c757d',
            padding: '15px',
            background: '#f8f9fa',
            borderRadius: '8px',
            textAlign: 'left',
            marginBottom: '15px'
          }}>
            <div style={{ marginBottom: '5px', fontWeight: '600' }}>Etat du chargement :</div>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '3px' }}>
              <span style={{ marginRight: '8px' }}>
                {(groups || rooms || []).length > 0 ? 'OK' : '...'}
              </span>
              Groupes: {(groups || rooms || []).length} charge(s)
            </div>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '3px' }}>
              <span style={{ marginRight: '8px' }}>
                {statuses.length > 0 ? 'OK' : '...'}
              </span>
              Statuts: {statuses.length} charge(s)
            </div>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '3px' }}>
              <span style={{ marginRight: '8px' }}>
                {(teamLeaders || []).length > 0 ? 'OK' : '...'}
              </span>
              Chefs d'equipe: {(teamLeaders || []).length} charge(s)
            </div>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '3px' }}>
              <span style={{ marginRight: '8px' }}>
                {users.length > 0 ? 'OK' : '...'}
              </span>
              Utilisateurs: {users.length} charge(s)
            </div>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <span style={{ marginRight: '8px' }}>
                {teams.length > 0 ? 'OK' : '...'}
              </span>
              Groupes: {teams.length} charge(s)
            </div>
          </div>
          
          {/* Affichage des erreurs */}
          {formError && (
            <div style={{ 
              marginBottom: '15px',
              padding: '10px',
              background: '#fee',
              color: '#c33',
              borderRadius: '8px',
              fontSize: '12px'
            }}>
              <strong>Erreur:</strong> {formError}
            </div>
          )}

          {/* Bouton de secours */}
          <button
            onClick={() => {
              console.log('🆘 Bouton de secours activé');
              setIsReady(true);
              stableShowInfo('Ouverture forcée', {
                title: '🆘 Mode de secours',
                details: 'Le formulaire s\'ouvre avec les données actuellement disponibles'
              });
            }}
            style={{
              padding: '12px 24px',
              background: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500'
            }}
          >
            🆘 Forcer l'ouverture
          </button>

          <div style={{ 
            fontSize: '11px', 
            color: '#999', 
            marginTop: '15px',
            padding: '8px',
            background: '#f8f9fa',
            borderRadius: '4px'
          }}>
            Debug: ID={componentId.current.slice(0, 6)} | Rooms={rooms.length} | Statuses={statuses.length} | Loading={formLoading}
          </div>
        </div>
      </div>
    );
  }

  // ✅ CONTENU PRINCIPAL (variable JSX, pas composant pour eviter re-mount)
  const formContent = (
    <div className="sf-container">
      {/* En-tete */}
      <SessionHeader
        editingSession={editingSession}
        sessionType={formData.prestation_type || formData.session_type}
        sessionTypes={PRESTATION_TYPES || SESSION_TYPES}
        onClose={handleClose}
      />

      {/* Navigation par onglets */}
      <TabNavigation
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        formData={formData}
      />

      {/* Contenu des onglets */}
      <div className="sf-content">
        {activeTab === 'basic' && (
          <BasicInfoPanel
            formData={formData}
            handleChange={handleChangeWithTracking}
            // Nouveau nommage
            groups={groups}
            clients={clients}
            locations={locations}
            teamLeaders={teamLeaders}
            // Alias pour compatibilite
            rooms={rooms}
            trainers={trainers}
            statuses={statuses}
            conflicts={conflicts}
            error={formError}
            multiDayInfo={multiDayInfo}
            token={token}
          />
        )}

        {activeTab === 'participants' && (
          <ParticipantsPanel
            formData={formData}
            setFormData={setFormDataWithTracking}
            users={users}
            teams={teams}
            teamLeaders={teamLeaders}
            token={token}
            startDate={formData.start_date}
            endDate={formData.end_date}
            sessionId={editingSession?.id || null}
            editingSession={editingSession}
            showSuccess={showSuccess}
            showError={showError}
            showWarning={showWarning}
            showInfo={showInfo}
            // Props multi-jours
            sessionDays={sessionDays}
            setSessionDays={setSessionDays}
            dayParticipants={dayParticipants}
            setDayParticipants={setDayParticipantsWithWarning}
            dayTeamLeaders={dayTeamLeaders}
            setDayTeamLeaders={setDayTeamLeaders}
            participantOnSite={participantOnSite}
            setParticipantOnSite={setParticipantOnSite}
            getParticipantOnSite={getParticipantOnSite}
            setParticipantOnSiteFlag={setParticipantOnSiteFlag}
            activeDayTab={activeDayTab}
            setActiveDayTab={setActiveDayTab}
            multiDayInfo={multiDayInfo}
          />
        )}

        {activeTab === 'options' && (
          <OptionsPanel
            formData={formData}
            setFormData={setFormDataWithTracking}
            handleChange={handleChangeWithTracking}
            statuses={statuses}
            multiDayInfo={multiDayInfo}
            editingSession={editingSession}
            errors={formError}
          />
        )}

        {activeTab === 'history' && editingSession?.id && (
          <EntityHistory
            entityType="prestation"
            entityId={editingSession.id}
            token={token}
            limit={50}
            showHeader={true}
            title="Historique de la prestation"
            compact={false}
          />
        )}
      </div>

      {/* Footer */}
      <SessionFooter
        formData={formData}
        statuses={statuses}
        onCancel={handleClose}
        onSubmit={handleSubmit}
        loading={loading}
        editingSession={editingSession}
        onOpenWorksheets={() => setShowWorksheets(true)}
        onOpenNotifications={() => setShowNotificationModal(true)}
      />

    </div>
  );

  // Modale des feuilles journalières (Portal pour sortir du contexte de stacking)
  const worksheetsModal = showWorksheets && editingSession?.id && ReactDOM.createPortal(
    <div className="worksheet-modal-overlay" style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.6)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '20px'
    }}>
      <WorksheetSelector
        prestationId={editingSession.id}
        prestationNumber={formData.prestation_number || editingSession.prestation_number}
        startDate={formData.start_date}
        endDate={formData.end_date}
        onClose={() => setShowWorksheets(false)}
      />
    </div>,
    document.body
  );

  // Modale de notification aux participants
  const notificationModal = showNotificationModal && editingSession?.id && ReactDOM.createPortal(
    <NotificationModal
      isOpen={showNotificationModal}
      onClose={() => setShowNotificationModal(false)}
      prestation={{
        id: editingSession.id,
        prestation_number: formData.prestation_number || editingSession.prestation_number,
        client_name: formData.client_name || clients.find(c => c.id === formData.client_id)?.name
      }}
      participants={users.filter(u => formData.participant_ids?.includes(u.id))}
      teamLeader={teamLeaders.find(t => t.id === formData.team_leader_id)}
      token={token}
    />,
    document.body
  );

  // ✅ RENDU FINAL
  if (editingSession?.id) {
    // Mode édition : ProtectedModal fournit son propre overlay
    return (
      <>
        <ProtectedModal
          modalType="session"
          modalId={editingSession.id.toString()}
          onLockAcquired={() => console.log('🔒 Verrou acquis')}
          onLockDenied={() => console.log('❌ Verrou refusé')}
          className="sf-protected-modal"
        >
          {formContent}
        </ProtectedModal>
        {worksheetsModal}
        {notificationModal}
      </>
    );
  }

  // Mode création : overlay simple
  return (
    <>
      <div className="sf-overlay">
        {formContent}
      </div>
      {worksheetsModal}
      {notificationModal}
    </>
  );
};

export default SessionForm;